import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button } from '@mui/material';
import { BoxProps } from '@mui/material/Box';
import LinkMenuIcon from '@/public/icons/link_menu_black.svg';
import { ApprovalType } from '@/types/api';
import { PAGE_URL } from '@/constant/url';
import { WorkEventName, WorkPageAreaType } from '@/constant/gtm';
import { NATIVE_APPROVAL_TYPES } from '@/constant';
import { useWorkEventTracker } from '@/hooks/gtm/useGTMEventTracker';
import { setJwtToken } from '@/utils/tokens';

export const NoticeBar = styled(Box)`
  background-color: rgba(0, 102, 204, 0.12);
  border-radius: 4px;
  padding: 16px 8px;
  line-height: 20px;
  font-size: 14px;
  color: var(--dk-accent, #0066cc);
`;

type ApprovalTagItemProps = {
  pageArea: WorkPageAreaType;
  title: string;
  approvalList: ApprovalType[];
  onClickDoc: (id: number) => void;
  onOpenWorkingHoursChange?: () => void;
  onOpenOvertimeRequest?: () => void;
  onOpenOvertimeAdjust?: () => void;
  onOpenAnnualLeave?: () => void;
} & BoxProps;
export const ApprovalTagItem = styled(
  ({
    pageArea,
    title,
    approvalList,
    onClickDoc,
    onOpenWorkingHoursChange,
    onOpenOvertimeRequest,
    onOpenOvertimeAdjust,
    onOpenAnnualLeave,
    ...props
  }: ApprovalTagItemProps) => {
    const workEventTracker = useWorkEventTracker(pageArea);
    const getUrl = (type: number) =>
      type === 221
        ? `${process.env.NEXT_PUBLIC_ORIGIN_URL}/app/ai-marketing-expense/fnco`
        : type === 212
        ? `${process.env.NEXT_PUBLIC_ORIGIN_URL}/app/ai-marketing-expense`
        : NATIVE_APPROVAL_TYPES.includes(type)
        ? `/approval/create?apv_type=${type}`
        : setJwtToken(
            process.env.NEXT_PUBLIC_IFRAME_URL + PAGE_URL.APPROVAL_CREATE(type),
          );

    const handleClick = (approval: ApprovalType) => {
      const { type, name } = approval;

      if (type === 212 || type === 221) {
        window.open(getUrl(type), '_blank', 'noreferrer');
      } else if (type === 222) {
        onOpenWorkingHoursChange?.();
      } else if (type === 223) {
        onOpenOvertimeRequest?.();
      } else if (type === 224) {
        onOpenOvertimeAdjust?.();
      } else if (type === 231) {
        onOpenAnnualLeave?.();
      } else {
        onClickDoc(type);
      }

      // GA Work - Approval 35번
      workEventTracker(WorkEventName.clickEvent, {
        area_name: '양식 목록',
        contents: name,
        status: true,
        description: '품의 양식 필터 선택',
      });
    };

    const handleLinkClick = (
      e: React.MouseEvent<HTMLAnchorElement>,
      approval: ApprovalType,
    ) => {
      e.stopPropagation();
      e.preventDefault();
      const { type, name } = approval;
      window.open(getUrl(type), '_blank', 'noreferrer');

      // GA Work - Approval 36번
      workEventTracker(WorkEventName.clickMore, {
        area_name: '양식 목록',
        button_text: name,
      });
    };

    return (
      <Box {...props}>
        <p className={'title'}>{title}</p>
        <div className={'approval-wrap'}>
          {approvalList.map((approval, index) => (
            <div key={index} className={'approval-item'}>
              <Button onClick={() => handleClick(approval)}>
                {approval.name}
                {!(
                  (approval.type >= 222 && approval.type <= 227) ||
                  approval.type === 231
                ) && (
                  <a
                    href={getUrl(approval.type)}
                    target={'_blank'}
                    rel="noreferrer"
                    onClick={(e) => handleLinkClick(e, approval)}
                  >
                    <LinkMenuIcon />
                  </a>
                )}
              </Button>
            </div>
          ))}
        </div>
      </Box>
    );
  },
)`
  width: 100%;
  padding: 20px 0;
  --approval-grid-gap: 12px;

  .title {
    margin: 0;
    padding-bottom: 12px;
    font-size: 20px;
    font-weight: 700;
    border-bottom: 1px solid black;
  }

  .approval-wrap {
    display: flex;
    flex-wrap: wrap;
    column-gap: ${'var(--approval-grid-gap)'};
    row-gap: calc(${'var(--approval-grid-gap)'} * 2);
    position: relative;
    top: ${'var(--approval-grid-gap)'};
    overflow: hidden;
  }

  .approval-item {
    width: calc((100% - (${'var(--approval-grid-gap)'} * 2)) / 3); /* 3컬럼 */
    position: relative;

    &:not(:first-child):nth-child(3n + 1)::before {
      content: '';
      position: absolute;
      top: calc(${'var(--approval-grid-gap)'} * -1);
      left: 0;
      width: 100vw;
      height: 1px;
      background-color: var(--dk-bg-strong, #e7e9ec);
    }

    button {
      width: 100%;
      display: flex;
      justify-content: space-between;
      padding: 8px 12px;
      font-size: 14px;
      color: var(--dk-text, black);
      line-height: 28px;
      a {
        display: none;
        width: 28px;
        height: 28px;
        padding: 4px;
        border-radius: 2px;
        &:hover {
          background-color: var(--dk-bg-strong, #e7e9ec);
        }
      }
      &:hover {
        background-color: var(--dk-bg-subtle, #f9fafb);
        a {
          display: block;
        }
      }
    }
  }
`;
